import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
} from '@angular/core';
import { DatastoreDocument } from '@freelancer/datastore';
import {
  ProfileViewUsersCollection,
  Qualification,
} from '@freelancer/datastore/collections';
import { AnchorScroll } from '@freelancer/ui';
import { Badge, BadgeType, ExamBadgeType } from '@freelancer/ui/badge';
import { ButtonColor, ButtonSize } from '@freelancer/ui/button';
import { CardSize } from '@freelancer/ui/card';
import { trackById } from '@freelancer/ui/helpers';
import { LinkHoverColor } from '@freelancer/ui/link';
import { ListItemPadding, ListItemType } from '@freelancer/ui/list-item';
import { Margin } from '@freelancer/ui/margin';
import { FontType, TextSize } from '@freelancer/ui/text';
import { isDefined } from '@freelancer/utils';
import * as Rx from 'rxjs';
import { map } from 'rxjs/operators';
import { UserProfileSection } from '../user-profile.component';

@Component({
  selector: 'app-user-profile-exams',
  template: `
    <ng-container *ngIf="!(profileViewUsersDocument.status$ | flAsync)?.error">
      <ng-container *ngIf="exams$ | flAsync as exams">
        <fl-card
          class="UserProfileExams"
          *ngIf="exams?.length > 0 || (isViewerProfileOwner$ | flAsync)"
          [size]="CardSize.SMALL"
        >
          <fl-card-header-title i18n="User certifications heading">
            Certifications
          </fl-card-header-title>

          <fl-list
            *ngIf="exams?.length > 0; else emptyState"
            [outerPadding]="false"
            [padding]="ListItemPadding.XXSMALL"
            [type]="ListItemType.NON_BORDERED"
            [flMarginBottom]="Margin.XSMALL"
          >
            <fl-list-item
              *ngFor="
                let exam of exams | slice: 0:displayCount;
                trackBy: trackById
              "
            >
              <fl-bit class="UserProfileExams-item">
                <fl-badge
                  *ngIf="exam.iconUrl"
                  class="UserProfileExams-badge"
                  [flMarginRight]="Margin.SMALL"
                  [badge]="getBadge(exam.iconUrl)"
                ></fl-badge>

                <fl-bit [flMarginRight]="Margin.XXXSMALL">
                  <!-- Do not link to exam purchase page when either logged out or the "exam" is not actually an exam -->
                  <ng-container
                    *ngIf="
                      (isLoggedIn$ | flAsync) && exam.name;
                      else loggedOutView
                    "
                  >
                    <fl-link
                      flTrackingLabel="ExamLinkToBuy"
                      [link]="'/exam/exams/buy.php'"
                      [queryParams]="{ id: exam.id }"
                      [hoverColor]="LinkHoverColor.INHERIT"
                    >
                      <ng-template
                        [ngTemplateOutlet]="loggedOutView"
                      ></ng-template>
                    </fl-link>
                  </ng-container>

                  <ng-template #loggedOutView>
                    <fl-text
                      class="UserProfileExams-name"
                      [fontType]="FontType.SPAN"
                      [flMarginRight]="Margin.SMALL"
                    >
                      {{ exam.type || exam.name || exam.description }}
                      {{ exam.level }}
                    </fl-text>
                  </ng-template>
                </fl-bit>

                <fl-text
                  class="UserProfileExams-value"
                  *ngIf="exam.scorePercentage"
                  [fontType]="FontType.SPAN"
                >
                  {{ exam.scorePercentage }}%
                </fl-text>
              </fl-bit>
            </fl-list-item>
          </fl-list>
          <fl-bit
            *ngIf="exams?.length > 0"
            class="UserProfileExams-viewMoreOrLess"
          >
            <fl-link
              *ngIf="exams?.length > displayCount"
              i18n="View more user certifications link"
              flTrackingLabel="ExamShowMoreLink"
              (click)="handleViewMore()"
            >
              View More
            </fl-link>
            <fl-link
              *ngIf="
                exams?.length <= displayCount &&
                displayCount > INITIAL_DISPLAY_COUNT
              "
              i18n="View less user certifications link"
              flTrackingLabel="ExamShowLessLink"
              (click)="handleViewLess()"
            >
              View Less
            </fl-link>
          </fl-bit>
          <ng-template #emptyState>
            <fl-bit class="UserProfileExams-emptyState">
              <fl-picture
                alt="Certifications are empty"
                i18n-alt="Empty certifications message"
                [flMarginBottom]="Margin.XSMALL"
                [src]="'profile/certifications.svg'"
              ></fl-picture>
              <fl-text
                i18n="Empty certifications message"
                [flMarginBottom]="Margin.XSMALL"
              >
                You don't have any certifications yet.
              </fl-text>
              <fl-button
                i18n="Get certified message"
                flTrackingLabel="UserProfileGetCertifiedButton"
                link="/exam/exams/index.php"
                [queryParams]="{ ttref: 'Exams_Profile' }"
                [color]="ButtonColor.DEFAULT"
                [display]="'block'"
              >
                Get Certified
              </fl-button>
            </fl-bit>
          </ng-template>
        </fl-card>
      </ng-container>
    </ng-container>
  `,
  styleUrls: ['./user-profile-exams.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserProfileExamsComponent implements OnInit {
  BadgeType = BadgeType;
  ButtonColor = ButtonColor;
  ButtonSize = ButtonSize;
  CardSize = CardSize;
  TextSize = TextSize;
  FontType = FontType;
  LinkHoverColor = LinkHoverColor;
  ListItemPadding = ListItemPadding;
  ListItemType = ListItemType;
  Margin = Margin;
  trackById = trackById;

  @Input()
  profileViewUsersDocument: DatastoreDocument<ProfileViewUsersCollection>;
  @Input() isLoggedIn$: Rx.Observable<boolean>;
  @Input() isViewerProfileOwner$: Rx.Observable<boolean>;

  readonly INITIAL_DISPLAY_COUNT = 5;

  displayCount = this.INITIAL_DISPLAY_COUNT;
  exams$: Rx.Observable<readonly Qualification[]>;

  constructor(private anchorScroll: AnchorScroll) {}

  ngOnInit() {
    this.exams$ = this.profileViewUsersDocument.valueChanges().pipe(
      map(user => {
        const exams = user.qualifications ? user.qualifications : [];
        return exams
          .filter(exam => isDefined(exam.name) || exam.iconName === 'vworker')
          .sort((a, b) => {
            if (a.scorePercentage && b.scorePercentage) {
              return b.scorePercentage - a.scorePercentage;
            }
            return 0;
          });
      }),
    );
  }

  handleViewLess() {
    this.displayCount = this.INITIAL_DISPLAY_COUNT;
    this.anchorScroll.scrollTo(UserProfileSection.EXAMS);
  }

  handleViewMore() {
    this.displayCount += this.INITIAL_DISPLAY_COUNT;
  }

  getBadge(icon: string): Badge {
    return {
      type: BadgeType.EXAM,
      badge: icon as ExamBadgeType,
    };
  }
}
